/**
 * @fileoverview CTA Links constants organized by profession
 * 
 * This module centralizes all call-to-action links used throughout the application,
 * organized by healthcare profession. This makes it easier to maintain and update
 * links when needed.
 * 
 * @author App'Ines Team
 * @version 1.0.0
 * @since 2025
 */

export const CTA_LINKS = {
  // Links for nurses (IDE - Infirmier Diplômé d'État)
  INFIRMIER: {
    // Main CV submission page from simulator
    CV_SUBMISSION: 'https://appines.fr/lp/k/ide-simulateur-salariat#cv',
    
    // Specific territory opportunities
    GUYANE_OPPORTUNITIES: 'https://appines.fr/lp/guyane/ide',
    
    // Future DOM-TOM opportunities (to be activated when available)
    GUADELOUPE_OPPORTUNITIES: 'https://appines.fr/lp/guadeloupe/ide', // Not yet available
    MARTINIQUE_OPPORTUNITIES: 'https://appines.fr/lp/martinique/ide', // Not yet available
    REUNION_OPPORTUNITIES: 'https://appines.fr/lp/reunion/ide', // Not yet available
    MAYOTTE_OPPORTUNITIES: 'https://appines.fr/lp/mayotte/ide', // Not yet available
    
    // General job opportunities
    JOB_OPPORTUNITIES: 'https://appines.fr/annonces/infirmier',
    
    // Career guidance
    CAREER_GUIDANCE: 'https://appines.fr/conseils/infirmier',
  },
  
  // Links for doctors (when the app expands to other professions)
  MEDECIN: {
    CV_SUBMISSION: 'https://appines.fr/lp/medecin/cv',
    JOB_OPPORTUNITIES: 'https://appines.fr/emploi/medecin',
    CAREER_GUIDANCE: 'https://appines.fr/conseils/medecin',
  },
  
  // Links for physiotherapists (when the app expands to other professions)
  KINESITHERAPEUTE: {
    CV_SUBMISSION: 'https://appines.fr/lp/kine/cv',
    JOB_OPPORTUNITIES: 'https://appines.fr/emploi/kinesitherapeute',
    CAREER_GUIDANCE: 'https://appines.fr/conseils/kinesitherapeute',
  },
  
  // General App'Ines links (not profession-specific)
  GENERAL: {
    // Main App'Ines website
    MAIN_WEBSITE: 'https://appines.fr/lp/k/ide-simulateur-salariat',
    
    // App download page
    APP_DOWNLOAD: 'https://appines.fr/app',
    
    // Social media and reviews
    SOCIAL_LINKS: 'https://linktr.ee/appines',
    
    // Support and contact
    SUPPORT_EMAIL: 'mailto:support@appines.fr',
    
    // Pay calendar page
    PAY_CALENDAR: '/paie-fonctionnaire',
    
    // Legal and privacy
    PRIVACY_POLICY: 'https://appines.fr/confidentialite',
    TERMS_OF_SERVICE: 'https://appines.fr/cgu',
  }
} as const;

// Helper function to get CTA links for a specific profession
export function getCTALinksForProfession(professionId: string) {
  switch (professionId) {
    case 'infirmier':
      return CTA_LINKS.INFIRMIER;
    case 'medecin':
      return CTA_LINKS.MEDECIN;
    case 'kinesitherapeute':
      return CTA_LINKS.KINESITHERAPEUTE;
    default:
      return CTA_LINKS.INFIRMIER; // Default to nurse links
  }
}

// Helper function to get the main CV submission link for a profession
export function getCVSubmissionLink(professionId: string): string {
  const links = getCTALinksForProfession(professionId);
  return links.CV_SUBMISSION;
}

// Helper function to get job opportunities link for a profession
export function getJobOpportunitiesLink(professionId: string): string {
  const links = getCTALinksForProfession(professionId);
  return links.JOB_OPPORTUNITIES;
}

// Helper function to get territory-specific job opportunities link
export function getTerritoryJobOpportunitiesLink(professionId: string, territoryId: string): string {
  const links = getCTALinksForProfession(professionId);
  
  // Map territory IDs to their specific opportunity links
  switch (territoryId) {
    case 'guyane':
      return links.GUYANE_OPPORTUNITIES;
    case 'guadeloupe':
      return links.GUADELOUPE_OPPORTUNITIES || links.JOB_OPPORTUNITIES; // Fallback to general
    case 'martinique':
      return links.MARTINIQUE_OPPORTUNITIES || links.JOB_OPPORTUNITIES; // Fallback to general
    case 'reunion':
      return links.REUNION_OPPORTUNITIES || links.JOB_OPPORTUNITIES; // Fallback to general
    case 'mayotte':
      return links.MAYOTTE_OPPORTUNITIES || links.JOB_OPPORTUNITIES; // Fallback to general
    default:
      return links.JOB_OPPORTUNITIES;
  }
}

// Helper function to check if territory-specific opportunities are available
export function isTerritoryOpportunitiesAvailable(territoryId: string): boolean {
  // Currently only Guyane has dedicated opportunities
  return territoryId === 'guyane';
}